import { useState, useMemo, useCallback } from 'react';
import useApiFetch from '@/hooks/useApiFetch';
import { getActiveOrgId } from '@/lib/activeOrg';
import financeClientsService, { MonthlyPnLData } from '@/services/financeClients';
import { FetchApiIds } from '@/types';
import FinanceXPnLComponent from './FinanceXPnL.component';

export type DatePreset =
  | 'this_month'
  | 'last_month'
  | 'this_quarter'
  | 'this_year'
  | 'last_year'
  | 'custom';

export type DateFilter = {
  preset: DatePreset;
  startDate: string;
  endDate: string;
};

function lastDayOfMonth(year: number, month: number): string {
  return new Date(year, month + 1, 0).toISOString().split('T')[0];
}

function getYearRange(year: number): { startDate: string; endDate: string } {
  return { startDate: `${year}-01-01`, endDate: `${year}-12-31` };
}

function getMonthRange(offset: number): { startDate: string; endDate: string } {
  const now = new Date();
  const target = new Date(now.getFullYear(), now.getMonth() + offset, 1);
  return {
    startDate: target.toISOString().split('T')[0],
    endDate: lastDayOfMonth(target.getFullYear(), target.getMonth()),
  };
}

function getQuarterRange(): { startDate: string; endDate: string } {
  const now = new Date();
  const q = Math.floor(now.getMonth() / 3);
  const start = new Date(now.getFullYear(), q * 3, 1);
  const endMonth = q * 3 + 2;
  return {
    startDate: start.toISOString().split('T')[0],
    endDate: lastDayOfMonth(now.getFullYear(), endMonth),
  };
}

const DEFAULT_PNL: MonthlyPnLData = {
  rows: [],
  ytdRevenue: 0,
  ytdCogs: 0,
  ytdGrossProfit: 0,
  ytdOpex: 0,
  ytdNetIncome: 0,
  ytdSavingsTarget: 0,
  ytdSurplus: 0,
  avgMonthlySavingsRate: 0,
  warnings: [],
};

const FinanceXPnLContainer = function (): JSX.Element {
  const currentYear = new Date().getFullYear();
  const orgId = getActiveOrgId();
  const [savingsTargetPct, setSavingsTargetPct] = useState(10);

  const [dateFilter, setDateFilter] = useState<DateFilter>({
    preset: 'this_year',
    ...getYearRange(currentYear),
  });

  const handlePresetChange = useCallback(
    (preset: DatePreset) => {
      switch (preset) {
        case 'this_month':
          setDateFilter({ preset, ...getMonthRange(0) });
          break;
        case 'last_month':
          setDateFilter({ preset, ...getMonthRange(-1) });
          break;
        case 'this_quarter':
          setDateFilter({ preset, ...getQuarterRange() });
          break;
        case 'this_year':
          setDateFilter({ preset, ...getYearRange(currentYear) });
          break;
        case 'last_year':
          setDateFilter({ preset, ...getYearRange(currentYear - 1) });
          break;
        default:
          setDateFilter((prev) => ({ ...prev, preset }));
      }
    },
    [currentYear]
  );

  const handleCustomDateChange = useCallback((startDate: string, endDate: string) => {
    setDateFilter({ preset: 'custom', startDate, endDate });
  }, []);

  const { data, isLoading } = useApiFetch<MonthlyPnLData, void>(
    [FetchApiIds.FX_DASHBOARD_SUMMARY, 'monthly-pnl', orgId, dateFilter.startDate, dateFilter.endDate, savingsTargetPct],
    async () => {
      const result = await financeClientsService.getMonthlyPnL(
        dateFilter.startDate,
        dateFilter.endDate,
        savingsTargetPct,
      );
      return { data: result } as any;
    },
    { retry: false }
  );

  const pnl = useMemo((): MonthlyPnLData => {
    const raw = data as any;
    const d = raw?.data ?? raw;
    return (d as MonthlyPnLData) ?? DEFAULT_PNL;
  }, [data]);

  const handleSavingsTargetChange = useCallback((pct: number) => {
    setSavingsTargetPct(pct);
  }, []);

  return (
    <FinanceXPnLComponent
      pnl={pnl}
      isLoading={isLoading}
      savingsTargetPct={savingsTargetPct}
      onSavingsTargetChange={handleSavingsTargetChange}
      dateFilter={dateFilter}
      onPresetChange={handlePresetChange}
      onCustomDateChange={handleCustomDateChange}
    />
  );
};

export default FinanceXPnLContainer;
